import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";

/**
 * Comparador entre avaliações de DOIS alunos diferentes (admin).
 * Props: students (array)
 */
export function CrossEvalCompare({ students = [] }) {
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [aEvals, setAEvals] = useState([]);
  const [bEvals, setBEvals] = useState([]);
  const [aEvalId, setAEvalId] = useState("");
  const [bEvalId, setBEvalId] = useState("");

  useEffect(() => {
    if (!aId) { setAEvals([]); setAEvalId(""); return; }
    api.get(`/evaluations/student/${aId}`)
      .then(r => {
        setAEvals(r.data);
        const last = r.data[r.data.length - 1];
        setAEvalId(last?.id || "");
      })
      .catch(err => toast.error(formatApiError(err)));
  }, [aId]);

  useEffect(() => {
    if (!bId) { setBEvals([]); setBEvalId(""); return; }
    api.get(`/evaluations/student/${bId}`)
      .then(r => {
        setBEvals(r.data);
        const last = r.data[r.data.length - 1];
        setBEvalId(last?.id || "");
      })
      .catch(err => toast.error(formatApiError(err)));
  }, [bId]);

  const a = aEvals.find(e => e.id === aEvalId);
  const b = bEvals.find(e => e.id === bEvalId);
  const aStudent = students.find(s => s.id === aId);
  const bStudent = students.find(s => s.id === bId);

  const keys = useMemo(() => {
    if (!a || !b) return [];
    return Array.from(new Set([...Object.keys(a.scores || {}), ...Object.keys(b.scores || {})]));
  }, [a, b]);

  const summary = keys.reduce(
    (acc, k) => {
      const av = a?.scores?.[k];
      const bv = b?.scores?.[k];
      if (av != null && bv != null) {
        if (bv > av) acc.up++;
        else if (bv < av) acc.down++;
        else acc.same++;
      }
      return acc;
    },
    { up: 0, down: 0, same: 0 }
  );

  return (
    <div className="vv-card p-5 space-y-4" data-testid="cross-eval-compare">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-blue))]">Atleta A</div>
          <Select value={aId} onValueChange={setAId}>
            <SelectTrigger data-testid="cross-a-student"><SelectValue placeholder="Selecione o atleta A" /></SelectTrigger>
            <SelectContent>
              {students.map(s => <SelectItem key={s.id} value={s.id}>{s.name} • {s.category}</SelectItem>)}
            </SelectContent>
          </Select>
          {aId && (
            aEvals.length === 0 ? (
              <div className="text-xs text-muted-foreground">Sem avaliações.</div>
            ) : (
              <Select value={aEvalId} onValueChange={setAEvalId}>
                <SelectTrigger data-testid="cross-a-eval"><SelectValue placeholder="Avaliação" /></SelectTrigger>
                <SelectContent>
                  {[...aEvals].reverse().map(e => <SelectItem key={e.id} value={e.id}>{e.date}</SelectItem>)}
                </SelectContent>
              </Select>
            )
          )}
        </div>
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Atleta B</div>
          <Select value={bId} onValueChange={setBId}>
            <SelectTrigger data-testid="cross-b-student"><SelectValue placeholder="Selecione o atleta B" /></SelectTrigger>
            <SelectContent>
              {students.map(s => <SelectItem key={s.id} value={s.id}>{s.name} • {s.category}</SelectItem>)}
            </SelectContent>
          </Select>
          {bId && (
            bEvals.length === 0 ? (
              <div className="text-xs text-muted-foreground">Sem avaliações.</div>
            ) : (
              <Select value={bEvalId} onValueChange={setBEvalId}>
                <SelectTrigger data-testid="cross-b-eval"><SelectValue placeholder="Avaliação" /></SelectTrigger>
                <SelectContent>
                  {[...bEvals].reverse().map(e => <SelectItem key={e.id} value={e.id}>{e.date}</SelectItem>)}
                </SelectContent>
              </Select>
            )
          )}
        </div>
      </div>

      {!a || !b ? (
        <div className="text-sm text-muted-foreground">Selecione um atleta e uma avaliação em cada lado para comparar.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Tile label={`B > A`} value={summary.up} color="lime" icon={TrendingUp} />
            <Tile label="Iguais" value={summary.same} color="yellow" icon={Minus} />
            <Tile label={`B < A`} value={summary.down} color="red" icon={TrendingDown} />
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="grid grid-cols-12 bg-muted/40 text-xs uppercase tracking-wider font-bold px-3 py-2">
              <div className="col-span-4">Critério</div>
              <div className="col-span-3 text-center">A — {aStudent?.name?.split(" ")[0]} ({a.date.slice(5)})</div>
              <div className="col-span-3 text-center">B — {bStudent?.name?.split(" ")[0]} ({b.date.slice(5)})</div>
              <div className="col-span-2 text-right">B − A</div>
            </div>
            {keys.map(k => {
              const av = a.scores?.[k] ?? null;
              const bv = b.scores?.[k] ?? null;
              const diff = (av != null && bv != null) ? bv - av : null;
              const color =
                diff == null ? "text-muted-foreground"
                : diff > 0 ? "text-[hsl(var(--vv-lime))]"
                : diff < 0 ? "text-destructive"
                : "text-muted-foreground";
              const Icon = diff == null ? Minus : diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
              return (
                <div key={k} className="grid grid-cols-12 items-center px-3 py-2 border-t border-border text-sm">
                  <div className="col-span-4 font-semibold">{k}</div>
                  <div className="col-span-3 text-center">{av ?? "—"}</div>
                  <div className="col-span-3 text-center font-bold">{bv ?? "—"}</div>
                  <div className={`col-span-2 flex items-center justify-end gap-1 font-black ${color}`}>
                    <Icon className="size-4" />
                    {diff == null ? "—" : (diff > 0 ? `+${diff}` : diff)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, icon: Icon, color }) {
  const c = {
    lime: "text-[hsl(var(--vv-lime))]",
    yellow: "text-[hsl(var(--vv-yellow))]",
    red: "text-destructive",
  }[color];
  return (
    <div className="rounded-lg bg-muted/30 border border-border p-3">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-3xl font-black ${c} flex items-center justify-center gap-1`}>
        <Icon className="size-5" /> {value}
      </div>
    </div>
  );
}
